import { DownOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { ColorPicker, Divider, Tooltip } from 'antd';
import { Color } from 'antd/lib/color-picker';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { useSaveMapConfigMutation } from 'entities/Floor';
import { SizeCode } from 'entities/SizeCode';
import React, { FC, ReactNode, memo, useCallback, useState } from 'react';
import { ReactComponent as AngleIcon } from 'shared/assets/icons/AngleIcon.svg';
import { ReactComponent as DeleteIcon } from 'shared/assets/icons/DeleteIcon.svg';
import { ReactComponent as StrokeWidthIcon } from 'shared/assets/icons/StrokeWidthIcon.svg';
import { Button } from 'shared/ui/Button';
import { Input } from 'shared/ui/Input';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { PIXELS_PER_METER } from '../../model/constants/mapScale';
import { getMapConfig } from '../../model/selectors/getMapConfig';
import { serializeMapConfig } from '../../model/mapConfigSerialization';
import { serializeShapes } from '../../model/serialization';
import { warehouseMapConstructorActions } from '../../model/slice/warehouseMapConstructorSlice';
import { isBoxShape, isLabeledShape, Shape, ShapeType, WarehouseMapTool } from '../../model/types';
import { Point } from '../../utils/helpers/coordinates';
import { getSharedField } from '../../utils/helpers/getSharedField';
import { BackgroundScaleMeasureApi } from '../../utils/hooks/useBackgroundScaleMeasure';
import { BoxPicker } from './BoxPicker';
import { NumericInput } from './NumericInput';
import { SizeCodeSelect } from './SizeCodeSelect';
import { SizeCodeSquareInfo } from './SizeCodeSquareInfo';
import { ImageTransformPanel } from './ImageTransformPanel';

interface ParametersBarProps {
  selectedShapes: Shape[];
  floorId: string;
  warehouseId: string | undefined;
  floorShapes: Shape[];
  isShapesChanged: boolean;
  isMapConfigChanged: boolean;
  availableSizeCodes: SizeCode[] | undefined;
  refetchFloors: () => void;
  getDropPoint: () => Point;
  activeTool: WarehouseMapTool;
  backgroundScaleMeasure: BackgroundScaleMeasureApi;
}

interface SidebarSectionProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}

const SidebarSection: FC<SidebarSectionProps> = ({ title, description, children }) => (
  <section className="rounded-xl border border-secondaryAccent bg-white p-4 shadow-sm">
    <div className="mb-3">
      <div className="font-medium text-primary">{title}</div>
      {description ? <div className="mt-0.5 text-xs text-primaryLight">{description}</div> : null}
    </div>
    <div className="space-y-2.5">{children}</div>
  </section>
);

export const ParametersBar: FC<ParametersBarProps> = memo((props) => {
  const {
    selectedShapes,
    floorId,
    warehouseId,
    floorShapes,
    refetchFloors,
    availableSizeCodes,
    isShapesChanged,
    isMapConfigChanged,
    getDropPoint,
    activeTool,
    backgroundScaleMeasure,
  } = props;

  const { t } = useAppTranslation('warehouse-management');
  const dispatch = useAppDispatch();
  const mapConfig = useAppSelector(getMapConfig);

  const selectedShape = selectedShapes[0];
  const isMultiSelect = selectedShapes.length > 1;
  const [isElementsOpen, setIsElementsOpen] = useState(false);

  const [saveMapConfigService, { isLoading }] = useSaveMapConfigMutation();

  const onSaveConfig = useCallback(async (): Promise<void> => {
    if (!warehouseId) return;

    await saveMapConfigService({
      floorId,
      warehouseId,
      args: {
        shapesConfig: serializeShapes(floorShapes),
        mapConfig: serializeMapConfig(mapConfig),
      },
    }).unwrap();
    refetchFloors();
  }, [floorId, floorShapes, mapConfig, refetchFloors, saveMapConfigService, warehouseId]);

  const updateSelectedShape = useCallback(
    (params: Partial<Shape>): void => {
      dispatch(warehouseMapConstructorActions.updateSelectedShape(params));
    },
    [dispatch],
  );

  const changeLabel = useCallback(
    (value: string): void => {
      updateSelectedShape({ label: value });
    },
    [updateSelectedShape],
  );

  const changeRotation = useCallback(
    (value: number): void => {
      if (isMultiSelect) {
        dispatch(warehouseMapConstructorActions.rotateSelectedShapes({ rotation: value }));
        return;
      }

      updateSelectedShape({ rotation: value });
    },
    [dispatch, isMultiSelect, updateSelectedShape],
  );

  const changeZIndex = useCallback(
    (value: number): void => {
      updateSelectedShape({ zIndex: roundNumber(value, 0) });
    },
    [updateSelectedShape],
  );

  const changeX = useCallback(
    (value: number): void => {
      updateSelectedShape({ x: value });
    },
    [updateSelectedShape],
  );

  const changeY = useCallback(
    (value: number): void => {
      updateSelectedShape({ y: value });
    },
    [updateSelectedShape],
  );

  const changeWidth = useCallback(
    (value: number): void => {
      updateSelectedShape({ width: value * PIXELS_PER_METER });
    },
    [updateSelectedShape],
  );

  const changeHeight = useCallback(
    (value: number): void => {
      updateSelectedShape({ height: value * PIXELS_PER_METER });
    },
    [updateSelectedShape],
  );

  const changeRadiusX = useCallback(
    (value: number): void => {
      updateSelectedShape({ radiusX: value * PIXELS_PER_METER });
    },
    [updateSelectedShape],
  );

  const changeRadiusY = useCallback(
    (value: number): void => {
      updateSelectedShape({ radiusY: value * PIXELS_PER_METER });
    },
    [updateSelectedShape],
  );

  const changeStrokeWidth = useCallback(
    (value: number): void => {
      updateSelectedShape({ strokeWidth: value });
    },
    [updateSelectedShape],
  );

  const changeLabelOffsetX = useCallback(
    (value: number): void => {
      updateSelectedShape({ labelOffsetX: value });
    },
    [updateSelectedShape],
  );

  const changeLabelOffsetY = useCallback(
    (value: number): void => {
      updateSelectedShape({ labelOffsetY: value });
    },
    [updateSelectedShape],
  );

  const changeStrokeColor = useCallback(
    (value: Color): void => {
      updateSelectedShape({ stroke: value.toRgbString() });
    },
    [updateSelectedShape],
  );

  const changeSizeCode = useCallback(
    (value: string): void => {
      updateSelectedShape({ sizeCodeId: value });
    },
    [updateSelectedShape],
  );

  const changeFontSize = useCallback(
    (value: number): void => {
      updateSelectedShape({ fontSize: value });
    },
    [updateSelectedShape],
  );

  const resetFontSize = useCallback((): void => {
    updateSelectedShape({ fontSize: undefined });
  }, [updateSelectedShape]);

  const changeLabelRotation = useCallback(
    (value: number): void => {
      updateSelectedShape({ labelRotation: value });
    },
    [updateSelectedShape],
  );

  const changeFloorLabelFontSize = useCallback(
    (value: number): void => {
      dispatch(warehouseMapConstructorActions.setLabelFontSize(value));
    },
    [dispatch],
  );

  const deleteSelected = useCallback((): void => {
    dispatch(warehouseMapConstructorActions.deleteSelected());
  }, [dispatch]);

  const lockShape = useCallback((): void => {
    if (selectedShape) {
      dispatch(warehouseMapConstructorActions.toggleLockShape(selectedShape.id));
    }
  }, [dispatch, selectedShape]);

  // ── Derived display values ───────────────────────────────────────────────────

  const label = selectedShape && 'label' in selectedShape ? selectedShape.label : undefined;
  const x = selectedShape ? roundNumber(selectedShape.x, 0).toString() : undefined;
  const y = selectedShape ? roundNumber(selectedShape.y, 0).toString() : undefined;
  const width =
    selectedShape && 'width' in selectedShape
      ? (roundNumber(selectedShape.width * selectedShape.scaleX, 0) / PIXELS_PER_METER).toString()
      : undefined;
  const height =
    selectedShape && 'height' in selectedShape
      ? (roundNumber(selectedShape.height * selectedShape.scaleY, 0) / PIXELS_PER_METER).toString()
      : undefined;
  const radiusX =
    selectedShape && 'radiusX' in selectedShape
      ? (roundNumber(selectedShape.radiusX * selectedShape.scaleX, 0) / PIXELS_PER_METER).toString()
      : undefined;
  const radiusY =
    selectedShape && 'radiusY' in selectedShape
      ? (roundNumber(selectedShape.radiusY * selectedShape.scaleY, 0) / PIXELS_PER_METER).toString()
      : undefined;
  const sizeCodeId = selectedShape && isBoxShape(selectedShape) ? selectedShape.sizeCodeId : undefined;
  const hasShapeLabel = Boolean(selectedShape && isLabeledShape(selectedShape));
  const fontSize =
    selectedShape && isLabeledShape(selectedShape) ? (selectedShape.fontSize ?? mapConfig.labelFontSize).toString() : undefined;
  const hasFontSizeOverride = Boolean(selectedShape && isLabeledShape(selectedShape) && selectedShape.fontSize !== undefined);
  const labelRotation = selectedShape && isBoxShape(selectedShape) ? (selectedShape.labelRotation ?? 0).toString() : undefined;

  // Shared values across all selected shapes (for multi-select controls).
  const sharedRotation = isMultiSelect
    ? getSharedField<number>(selectedShapes, 'rotation')
    : selectedShape
    ? roundNumber(selectedShape.rotation, 0)
    : undefined;
  const sharedRotationStr = sharedRotation !== undefined ? sharedRotation.toString() : undefined;

  const sharedZIndex = isMultiSelect
    ? getSharedField<number>(selectedShapes, 'zIndex')
    : selectedShape
    ? roundNumber(selectedShape.zIndex, 0)
    : undefined;
  const sharedZIndexStr = sharedZIndex !== undefined ? sharedZIndex.toString() : undefined;

  const sharedStroke = isMultiSelect
    ? getSharedField<string>(selectedShapes, 'stroke')
    : selectedShape && 'stroke' in selectedShape
    ? (selectedShape as { stroke: string }).stroke
    : undefined;
  const color = sharedStroke;

  const sharedStrokeWidth = isMultiSelect
    ? getSharedField<number>(selectedShapes, 'strokeWidth')
    : selectedShape && 'strokeWidth' in selectedShape
    ? roundNumber((selectedShape as { strokeWidth: number }).strokeWidth, 0)
    : undefined;
  const strokeWidth = sharedStrokeWidth !== undefined ? sharedStrokeWidth.toString() : undefined;

  // ── Rotation for single-select (unchanged) ──────────────────────────────────
  const singleRotation = selectedShape ? roundNumber(selectedShape.rotation, 0).toString() : undefined;

  const hasLabelOffset = selectedShape?.type === ShapeType.RECTANGLE || selectedShape?.type === ShapeType.POLYGON_BOX;
  const labelOffsetX = hasLabelOffset && 'labelOffsetX' in selectedShape ? (selectedShape.labelOffsetX ?? 0).toString() : undefined;
  const labelOffsetY = hasLabelOffset && 'labelOffsetY' in selectedShape ? (selectedShape.labelOffsetY ?? 0).toString() : undefined;

  return (
    <aside className="absolute bottom-4 right-4 top-4 z-10 flex w-[400px] max-w-[calc(100%-2rem)] flex-col overflow-hidden rounded-2xl border border-secondaryAccent bg-white shadow-xl">
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-secondaryAccent bg-white px-5 py-4">
        <div className="min-w-0">
          <div className="text-lg font-semibold text-primary">{t('Parameters')}</div>
          <div className="truncate text-xs text-primaryLight">
            {isShapesChanged || isMapConfigChanged ? t('Unsaved changes') : t('No unsaved changes')}
          </div>
        </div>
        <Tooltip title={isShapesChanged || isMapConfigChanged ? '' : t('No unsaved changes')}>
          <Button
            containerClassName="shrink-0"
            className="min-w-[112px] !py-2.5"
            onClick={onSaveConfig}
            isLoading={isLoading}
            isDisabled={!isShapesChanged && !isMapConfigChanged}
          >
            {t('Save')}
          </Button>
        </Tooltip>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-secondaryLight p-4">
        <SidebarSection title={t('Floor labels')}>
          <NumericInput
            className="!h-9"
            prefix={t('Font size')}
            value={mapConfig.labelFontSize.toString()}
            min={1}
            onCommit={changeFloorLabelFontSize}
          />
        </SidebarSection>
        <div className="space-y-3">
          {activeTool === WarehouseMapTool.EDIT_BACKGROUND ? (
            <SidebarSection title={t('Background image')}>
              <ImageTransformPanel backgroundScaleMeasure={backgroundScaleMeasure} />
            </SidebarSection>
          ) : isMultiSelect ? (
            // ── Multi-select: common fields only ──────────────────────────────
            <SidebarSection title={t('Selected shapes')} description={t('{{count}} shapes selected', { count: selectedShapes.length })}>
              <div className="grid grid-cols-2 gap-2.5">
                <NumericInput className="!h-9" prefix={<AngleIcon />} value={sharedRotationStr} onCommit={changeRotation} />
                <NumericInput className="!h-9" prefix={t('Z-index')} value={sharedZIndexStr} onCommit={changeZIndex} />
              </div>
              <button
                type="button"
                className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-error/30 text-sm text-error transition-colors hover:bg-error/5"
                onClick={deleteSelected}
              >
                <DeleteIcon />
                {t('Delete selected')}
              </button>
            </SidebarSection>
          ) : selectedShape ? (
            // ── Single-select: full per-variant form ──────────────────────────
            <SidebarSection title={t('Selected shape')} description={label || t('Unnamed shape')}>
              <div className="flex items-center space-x-2.5 [&>div]:flex-1 [&>span]:flex-1">
                <Input size="extraSmall" bordered placeholder={t('Name')} value={label} onChange={changeLabel} />
                <NumericInput className="!h-8" prefix={<AngleIcon />} value={singleRotation} onCommit={changeRotation} />
              </div>
              <div className="flex items-center space-x-2.5">
                <NumericInput className="!h-8 flex-1" prefix={t('X')} value={x} onCommit={changeX} />
                <NumericInput className="!h-8 flex-1" prefix={t('Y')} value={y} onCommit={changeY} />
              </div>
              <NumericInput className="!h-8" prefix={t('Z-index')} value={sharedZIndexStr} onCommit={changeZIndex} />
              <div className="flex items-center space-x-2.5">
                {!radiusX && !radiusY ? (
                  <>
                    <NumericInput
                      className="!h-8 flex-1"
                      prefix={t('W')}
                      value={width}
                      disabled={!width}
                      min={0.01}
                      onCommit={changeWidth}
                    />
                    <NumericInput
                      className="!h-8 flex-1"
                      prefix={t('H')}
                      value={height}
                      disabled={!height}
                      min={0.01}
                      onCommit={changeHeight}
                    />
                  </>
                ) : (
                  <>
                    <NumericInput className="!h-8 flex-1" prefix={t('Radius X')} value={radiusX} min={0.01} onCommit={changeRadiusX} />
                    <NumericInput className="!h-8 flex-1" prefix={t('Radius Y')} value={radiusY} min={0.01} onCommit={changeRadiusY} />
                  </>
                )}
              </div>
              {selectedShape?.type === ShapeType.RECTANGLE && (
                <>
                  <SizeCodeSelect warehouseId={warehouseId} value={sizeCodeId} onChange={changeSizeCode} />
                  {availableSizeCodes && availableSizeCodes.length > 0 && (
                    <SizeCodeSquareInfo shape={selectedShape} availableSizeCodes={availableSizeCodes} onReset={changeSizeCode} />
                  )}
                </>
              )}
              {hasShapeLabel && (
                <>
                  <Divider className="!my-2 border-secondaryAccent" />
                  <div className="mb-2 text-sm">{t('Shape label')}</div>
                  <div className="flex items-center space-x-2.5">
                    <NumericInput className="!h-8 flex-1" prefix={t('Font size')} value={fontSize} min={1} onCommit={changeFontSize} />
                    {labelRotation !== undefined && (
                      <NumericInput className="!h-8 flex-1" prefix={t('Rotation')} value={labelRotation} onCommit={changeLabelRotation} />
                    )}
                  </div>
                  {hasFontSizeOverride && (
                    <Button theme="clear" size="small" onClick={resetFontSize}>
                      {t('Use floor font size')}
                    </Button>
                  )}
                </>
              )}
              {hasLabelOffset && (
                <>
                  <Divider className="!my-2 border-secondaryAccent" />
                  <div className="mb-2 text-sm">{t('Label position')}</div>
                  <div className="flex items-center space-x-2.5">
                    <NumericInput className="!h-8 flex-1" prefix={t('X')} value={labelOffsetX} onCommit={changeLabelOffsetX} />
                    <NumericInput className="!h-8 flex-1" prefix={t('Y')} value={labelOffsetY} onCommit={changeLabelOffsetY} />
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-2.5 !mt-4">
                <button
                  type="button"
                  className="flex h-9 items-center justify-center gap-2 rounded-lg border border-error/30 text-sm text-error transition-colors hover:bg-error/5"
                  onClick={deleteSelected}
                >
                  <DeleteIcon />
                  {t('Delete')}
                </button>
                <button
                  type="button"
                  className="flex h-9 items-center justify-center gap-2 rounded-lg border border-secondaryAccent text-sm text-primary transition-colors hover:bg-secondaryLight"
                  onClick={lockShape}
                >
                  {selectedShape.locked ? <LockOutlined className="text-error" /> : <UnlockOutlined />}
                  {selectedShape.locked ? t('Unlock') : t('Lock')}
                </button>
              </div>
            </SidebarSection>
          ) : (
            <SidebarSection title={t('Selected shape')}>
              <div className="rounded-lg bg-secondaryLight px-3 py-4 text-center text-sm text-primaryLight">
                {t('Select a shape on the map to edit its parameters')}
              </div>
            </SidebarSection>
          )}
        </div>

        {/* Stroke section — shown whenever any shape is selected; callbacks naturally apply to all selected */}
        {activeTool !== WarehouseMapTool.EDIT_BACKGROUND && selectedShapes.length > 0 && (
          <SidebarSection title={t('Appearance')}>
            <div className="flex items-center space-x-2.5">
              <ColorPicker
                className="h-9 shrink-0 [&>.ant-color-picker-color-block]:h-[20px] [&>.ant-color-picker-color-block]:w-[20px]"
                defaultValue="black"
                showText={(c) => (
                  <div>
                    {c.toHexString().toUpperCase()}
                    <span className="text-secondaryLight inline-block mx-3.5">|</span>
                    {roundNumber(c.toRgb().a * 100, 0)}%
                  </div>
                )}
                value={color}
                onChange={changeStrokeColor}
              />
              <NumericInput className="!h-9 flex-1" prefix={<StrokeWidthIcon />} value={strokeWidth} min={0} onCommit={changeStrokeWidth} />
            </div>
          </SidebarSection>
        )}

        <section className="overflow-hidden rounded-xl border border-secondaryAccent bg-white shadow-sm">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 p-4 text-left font-medium text-primary"
            aria-expanded={isElementsOpen}
            onClick={() => {
              setIsElementsOpen((isOpen) => !isOpen);
            }}
          >
            {t('Elements')}
            <DownOutlined className={`text-xs transition-transform ${isElementsOpen ? 'rotate-180' : ''}`} />
          </button>
          {isElementsOpen && (
            <div className="border-t border-secondaryAccent p-4">
              <BoxPicker floorId={floorId} getDropPoint={getDropPoint} />
            </div>
          )}
        </section>
      </div>
    </aside>
  );
});
